// frontend/src/App.js
import React, { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import './App.css';
import './MobileResponsive.css';
import './MobileWorkspaceEnhancements.css';
import './ActivityNotesEnhancements.css';
import Sidebar from './components/Sidebar';
import PanelControl from './components/PanelControl';
import Leads from './components/Leads';
import Clientes from './components/Clientes';
import Propuestas from './components/Propuestas';
import Documentos from './components/Documentos';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ContractSignature from './pages/ContractSignature';
import ClientPortal from './pages/ClientPortal';
import Tareas from './components/Tareas';
import EOIR from './components/EOIR';
import CasePortfolio from './components/CasePortfolio';
import Users from './components/Users';
import Settings from './components/Settings';
import USCISCases from './components/USCISCases';
import AutomatizacionIA from './components/AutomatizacionIA';
import PausaActiva from './components/PausaActiva';
import CalendarHub from './components/CalendarHub';
import Marketing from './components/Marketing';
import Accidents from './components/Accidents';
import WorkspaceToolbar from './components/WorkspaceToolbar';
import Checklist from './components/Checklist';
import Consular from './components/Consular';
import DriveImport from './components/DriveImport';
import EstatePlanning from './components/EstatePlanning';
import PaymentsWorkspace from './components/PaymentsWorkspace';
import RegistroActividades from './components/RegistroActividades';
import ReportProblem from './components/ReportProblem';
import './DesignSystem.css';
import { formatDateUS } from './utils/dateFormat';
import { isPersonalInjuryRole, mayOpenSection } from './accessPolicy';

// ✅ NUEVO - Communication Hub Components
import CommunicationHub from './components/communication/CommunicationHub';
import QLFConecta from './components/QLFConecta';

// Componente para rutas protegidas
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader"></div>
        <p>Cargando...</p>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Componente del layout principal
const CRM_SECTIONS = new Set([
  'panel_control', 'leads', 'clientes', 'enviar_propuesta_contrato', 'documentos', 'importar_drive',
  'tareas', 'checklist', 'pagos', 'registro_actividades', 'consulado_colombia', 'casos_eoir_bia',
  'casos_civiles', 'casos_accidentes', 'estate_planning_trust', 'casos_uscis', 'automatizacion_ia',
  'calendario', 'marketing', 'usuarios', 'configuracion', 'comunicaciones', 'whatsapp_lite', 'qlf_conecta', 'pausa_activa',
]);

const crmLocation = () => {
  const params = new URLSearchParams(window.location.search);
  const section = window.history.state?.crmSection || params.get('section') || 'panel_control';
  const clientId = window.history.state?.crmClientId || params.get('client') || null;
  return { section: CRM_SECTIONS.has(section) ? section : 'panel_control', clientId };
};

const MainLayout = () => {
  const { usuario } = useAuth();
  const personalInjury = isPersonalInjuryRole(usuario?.rol);
  const initialLocation = crmLocation();
  const [activeSection, setActiveSection] = useState(initialLocation.section);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [clientWorkspaceId, setClientWorkspaceId] = useState(initialLocation.clientId);
  const [isMobile, setIsMobile] = useState(false);
  const changeSection = useCallback((target, options = {}) => {
    const entry = target && typeof target === 'object' ? target : null;
    const requestedSection = entry?.section || target;
    const section = CRM_SECTIONS.has(requestedSection) ? requestedSection : 'panel_control';
    const clientId = section === 'clientes' ? (entry?.id || null) : null;
    // La búsqueda global entrega el identificador del registro. Conservarlo
    // permite abrir el expediente del cliente, no únicamente la cartera.
    setClientWorkspaceId(clientId);
    setActiveSection(section);
    setMobileMenuOpen(false);
    const url = new URL(window.location.href);
    if (section === 'panel_control') url.searchParams.delete('section');
    else url.searchParams.set('section', section);
    if (clientId) url.searchParams.set('client', clientId);
    else url.searchParams.delete('client');
    const state = { ...(window.history.state || {}), crmSection: section, crmClientId: clientId };
    delete state.crmWhatsappConversationId;
    const method = options.replace ? 'replaceState' : 'pushState';
    window.history[method](state, '', `${url.pathname}${url.search}${url.hash}`);
  }, []);

  useEffect(() => {
    const current = personalInjury && !new URLSearchParams(window.location.search).has('section')
      ? { section: 'casos_accidentes', clientId: null }
      : crmLocation();
    // A direct reload inside a module otherwise has no in-app history entry
    // and the first browser-back gesture leaves the CRM. Seed one dashboard
    // entry, then restore the requested module as the current entry.
    if (!window.history.state?.crmHistoryReady && current.section !== 'panel_control') {
      const dashboardUrl = new URL(window.location.href);
      dashboardUrl.searchParams.delete('section');
      dashboardUrl.searchParams.delete('client');
      window.history.replaceState(
        { ...(window.history.state || {}), crmHistoryReady: true, crmSection: 'panel_control', crmClientId: null },
        '',
        `${dashboardUrl.pathname}${dashboardUrl.search}${dashboardUrl.hash}`,
      );
      changeSection({ section: current.section, id: current.clientId });
    } else {
      const readyState = { ...(window.history.state || {}), crmHistoryReady: true };
      window.history.replaceState(readyState, '', window.location.href);
      changeSection({ section: current.section, id: current.clientId }, { replace: true });
    }
    const restoreNavigation = () => {
      const location = crmLocation();
      setActiveSection(location.section);
      setClientWorkspaceId(location.clientId);
      setMobileMenuOpen(false);
    };
    window.addEventListener('popstate', restoreNavigation);
    return () => window.removeEventListener('popstate', restoreNavigation);
  }, [changeSection, personalInjury]);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px), (pointer: coarse) and (max-width: 1024px)');
    const updateDevice = () => {
      const mobile = media.matches;
      setIsMobile(mobile);
      document.documentElement.dataset.device = mobile ? 'mobile' : 'desktop';
      if (!mobile) setMobileMenuOpen(false);
    };
    updateDevice();
    media.addEventListener('change', updateDevice);
    return () => media.removeEventListener('change', updateDevice);
  }, []);

  useEffect(() => {
    // Mobile browsers resize their visible viewport when browser chrome or the
    // keyboard opens. Viewport-owned workspaces must follow that real height.
    const viewport = window.visualViewport;
    const syncVisibleViewport = () => {
      const visibleHeight = viewport?.height || window.innerHeight;
      document.documentElement.style.setProperty('--qlf-visible-viewport-height', `${Math.round(visibleHeight)}px`);
    };
    syncVisibleViewport();
    window.addEventListener('resize', syncVisibleViewport);
    viewport?.addEventListener('resize', syncVisibleViewport);
    viewport?.addEventListener('scroll', syncVisibleViewport);
    return () => {
      window.removeEventListener('resize', syncVisibleViewport);
      viewport?.removeEventListener('resize', syncVisibleViewport);
      viewport?.removeEventListener('scroll', syncVisibleViewport);
    };
  }, []);

  const renderContent = () => {
    if (!mayOpenSection(usuario?.rol, activeSection)) {
      return <section className="crm-access-note" role="status"><h2>Tu espacio de trabajo está en Accidentes</h2><p>Para cuidar la información de cada cliente, este acceso se concentra en los casos de accidentes y sus expedientes. Puedes continuar allí, importar desde Drive o actualizar tu perfil.</p><button type="button" onClick={() => changeSection('casos_accidentes')}>Ir a mis casos de accidentes</button></section>;
    }
    switch(activeSection) {
      case 'panel_control':
        return <PanelControl onNavigate={(target) => changeSection(target === 'eoir' ? 'casos_eoir_bia' : target === 'civil' ? 'casos_civiles' : target === 'accidentes' ? 'casos_accidentes' : target === 'uscis' ? 'casos_uscis' : target)} />;
      case 'leads':
        return <Leads />;
      case 'clientes':
        return <Clientes initialClientId={clientWorkspaceId} onProfileClosed={() => changeSection('clientes', { replace: true })} onCaseCreated={(area) => changeSection(area === 'eoir' ? 'casos_eoir_bia' : area === 'civil' ? 'casos_civiles' : area === 'accidentes' ? 'casos_accidentes' : 'casos_uscis')} />;
      case 'enviar_propuesta_contrato':
        return <Propuestas />;
      case 'documentos':
        return <Documentos />;
      case 'importar_drive':
        return <DriveImport />;
      case 'tareas':
        return <Tareas />;
      case 'checklist':
        return <Checklist />;
      case 'pagos':
        return <PaymentsWorkspace />;
      case 'registro_actividades':
        return <RegistroActividades />;
      case 'consulado_colombia':
        return <Consular />;
      case 'casos_eoir_bia':
        return <EOIR onOpenClient={(clientId) => changeSection({ section: 'clientes', id: clientId })} />;
      case 'casos_civiles':
        return <CasePortfolio area="civil" />;
      case 'casos_accidentes':
        return <Accidents />;
      case 'estate_planning_trust':
        return <EstatePlanning />;
      case 'casos_uscis':
        return <USCISCases />;
      case 'automatizacion_ia':
        return <AutomatizacionIA />;
      case 'pausa_activa':
        return <PausaActiva />;
      case 'calendario':
        return <CalendarHub />;
      case 'marketing':
        return <Marketing />;
      case 'usuarios':
        return <Users />;
      case 'configuracion':
        return <Settings />;
      // ✅ NUEVO - Communication Hub
      case 'comunicaciones':
        return <CommunicationHub />;
      case 'whatsapp_lite':
        return <CommunicationHub liteMode />;
      case 'qlf_conecta':
        return <QLFConecta />;
      default:
        return <PanelControl />;
    }
  };

  const today = new Date();
  const fullDateLabel = formatDateUS(today);
  const compactDateLabel = formatDateUS(today);

  return (
    <div className={`App ${isMobile ? 'mobile-layout' : 'desktop-layout'}`}>
      <Sidebar onSectionChange={changeSection} activeSection={activeSection} mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />
      <div className="main-content">
        <header className="App-header">
          <div className="header-content">
            <div className="header-title-block">
              <button className="mobile-menu-button" type="button" aria-label="Abrir navegación" onClick={() => setMobileMenuOpen(true)}>☰</button>
            </div>
            <div className="header-right">
              <div className="header-date" title={fullDateLabel} aria-label={`Fecha de hoy: ${fullDateLabel}`}>
                <span className="header-date-long">{fullDateLabel}</span>
                <span className="header-date-short">{compactDateLabel}</span>
              </div>
              {!personalInjury && <WorkspaceToolbar onNavigate={changeSection} />}
            </div>
          </div>
        </header>
        <main>
          {renderContent()}
        </main>
        {!personalInjury && <ReportProblem />}
      </div>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/firmar/:token" element={<ContractSignature />} />
        {/* Enlaces históricos: /expediente/<token>.  Los nuevos usan una
            etiqueta legible y un token independiente, corto y seguro. */}
        <Route path="/expediente/:token" element={<ClientPortal />} />
        <Route path="/e/:label/:token" element={<ClientPortal />} />
        <Route path="/" element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
